import {
  type ConnectionType,
  type DiagramDocument,
  type DeviceType,
} from '@ukrop-nm/shared';

function xmlEsc(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function cellId(prefix: string, raw: string, index: number): string {
  const safe = raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40);
  return safe ? `${prefix}-${safe}` : `${prefix}-${index}`;
}

function nodeStyle(type: DeviceType): string {
  if (type === 'zone') {
    return 'swimlane;html=1;whiteSpace=wrap;ukropType=zone;';
  }
  return `rounded=1;whiteSpace=wrap;html=1;ukropType=${type};`;
}

function edgeStyle(conn: ConnectionType): string {
  return `endArrow=classic;html=1;rounded=0;ukropConn=${conn};`;
}

/** Uncompressed mxfile so diagrams.net can open it without extra tools. */
export function exportDrawioXml(doc: DiagramDocument): string {
  const zones = doc.nodes.filter((n) => n.type === 'zone');
  const devices = doc.nodes.filter((n) => n.type !== 'zone');
  const idMap = new Map<string, string>();

  zones.forEach((n, i) => idMap.set(n.id, cellId('z', n.id, i + 2)));
  devices.forEach((n, i) => idMap.set(n.id, cellId('n', n.id, i + 2 + zones.length)));

  const lines: string[] = [];
  lines.push('<mxCell id="0"/>');
  lines.push('<mxCell id="1" parent="0"/>');

  for (const z of zones) {
    const id = idMap.get(z.id)!;
    const w = Math.round(z.size?.width ?? 320);
    const h = Math.round(z.size?.height ?? 220);
    const x = Math.round(z.position.x);
    const y = Math.round(z.position.y);
    const extra = z.monitorKey ? `ukropKey=${xmlEsc(z.monitorKey)};` : '';
    lines.push(
      `<mxCell id="${xmlEsc(id)}" value="${xmlEsc(z.label)}" style="${nodeStyle('zone')}${extra}" vertex="1" parent="1">`,
      `<mxGeometry x="${x}" y="${y}" width="${w}" height="${h}" as="geometry"/>`,
      '</mxCell>',
    );
  }

  for (const n of devices) {
    const id = idMap.get(n.id)!;
    const parentMx = n.zoneId && idMap.has(n.zoneId) ? idMap.get(n.zoneId)! : '1';
    const parent = n.zoneId ? doc.nodes.find((z) => z.id === n.zoneId) : undefined;
    const x = Math.round(n.position.x - (parent?.position.x ?? 0));
    const y = Math.round(n.position.y - (parent?.position.y ?? 0));
    const w = Math.round(n.size?.width ?? 160);
    const h = Math.round(n.size?.height ?? 90);
    const key = n.monitorKey ? `ukropKey=${xmlEsc(n.monitorKey)};` : '';
    const title = n.label;
    lines.push(
      `<mxCell id="${xmlEsc(id)}" value="${xmlEsc(title)}" style="${nodeStyle(n.type)}${key}" vertex="1" parent="${xmlEsc(parentMx)}">`,
      `<mxGeometry x="${x}" y="${y}" width="${w}" height="${h}" as="geometry"/>`,
      '</mxCell>',
    );
  }

  doc.edges.forEach((e, i) => {
    const source = idMap.get(e.sourceNodeId);
    const target = idMap.get(e.targetNodeId);
    if (!source || !target) return;
    const cidr = e.networkCidr ? `ukropCidr=${xmlEsc(e.networkCidr)};` : '';
    const ps = e.sourcePort ? `ukropPortS=${xmlEsc(e.sourcePort)};` : '';
    const pt = e.targetPort ? `ukropPortT=${xmlEsc(e.targetPort)};` : '';
    const eid = cellId('e', e.id, i);
    lines.push(
      `<mxCell id="${xmlEsc(eid)}" value="${xmlEsc(e.label || e.monitorKey || '')}" style="${edgeStyle(e.connectionType)}${cidr}${ps}${pt}" edge="1" parent="1" source="${xmlEsc(source)}" target="${xmlEsc(target)}">`,
      '<mxGeometry relative="1" as="geometry"/>',
      '</mxCell>',
    );
  });

  const name = xmlEsc(doc.name || doc.siteId);
  return `<?xml version="1.0" encoding="UTF-8"?>
<mxfile host="UKROP Network Monitor" agent="ukrop-nm" version="24.0.0" type="device">
  <diagram id="${xmlEsc(doc.id || doc.siteId)}" name="${name}">
    <mxGraphModel dx="0" dy="0" grid="1" gridSize="16" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="1600" pageHeight="900">
      <root>
        ${lines.join('\n        ')}
      </root>
    </mxGraphModel>
  </diagram>
</mxfile>
`;
}
